class ProductPage {
  constructor() {
    this.productEl = document.querySelector(".product");
    if (!this.productEl) return;

    this.productId = this.productEl.dataset.productId;
    this.productData = this.getProductData();

    this.Swiper = this.initHeroSwiper();

    this.initInfoAccordion();
    this.initQuantitySelector();

    if (!this.productData) return;

    this.initBundle();
    this.initVariantLogic();
    this.syncFromURL();
  }

  /* -----------------------------------------------------------------------
     BUNDLE PICKS (nested under the product via parent_id)
  ----------------------------------------------------------------------- */
  initBundle() {
    this.bundleEl = this.productEl.querySelector("[data-bundle]");
    if (!this.bundleEl) return;

    this.bundleEl.addEventListener("change", (e) => {
      if (e.target.matches("[data-bundle-pick]")) this.updateBundle();
    });

    const uid = this.productEl.querySelector("[data-bundle-uid]");
    uid?.form.addEventListener("submit", () => {
      uid.value = ProductPage.bundleLabel(uid.form);
    });
  }

  /* Packing summary for the admin order page, e.g. "2x 898508 Milk & Dark, 1x 896407 Gold Mini Whisk (ref k3x9ab)".
     The ref keeps it unique per add, or a second bundle merges into the first line. */
  static bundleLabel(form) {
    const counts = new Map();
    [
      ...[...form.querySelectorAll("[data-bundle-pick]")].map((select) => select.selectedOptions[0]),
      form.querySelector("[data-bundle-gift]"),
    ]
      .filter(Boolean)
      .forEach((el) => {
        const key = [el.dataset.sku, el.dataset.name].filter(Boolean).join(" ");
        counts.set(key, (counts.get(key) || 0) + 1);
      });
    const contents = [...counts].map(([item, qty]) => `${qty}x ${item}`).join(", ");
    const ref = Date.now().toString(36).slice(-4) + Math.random().toString(36).slice(2, 4);
    return `${contents} (ref ${ref})`.trim();
  }

  updateBundle() {
    if (!this.bundleEl || !this.currentVariant) return;

    this.bundleEl.querySelectorAll("[data-bundle-parent]").forEach((input) => (input.value = this.currentVariant.id));

    // Product price + each pick's uplift over the cheapest blend (a discount makes the packs free)
    const base = parseInt(this.bundleEl.dataset.bundleBase, 10) || 0;
    const picks = [...this.bundleEl.querySelectorAll("[data-bundle-pick]")].map(
      (select) => parseInt(select.selectedOptions[0]?.dataset.price, 10) || 0,
    );
    const uplift = picks.reduce((sum, price) => sum + Math.max(0, price - base), 0);
    const total = this.currentVariant.price + uplift;
    const compare = (this.currentVariant.compare_at_price || 0) + uplift;
    const formatted = ProductPage.formatCents(total, this.bundleEl.dataset.moneyFormat);

    if (this.priceOg) this.priceOg.innerHTML = formatted;
    if (this.priceSale) {
      const onSale = this.currentVariant.compare_at_price > this.currentVariant.price;
      this.priceSale.style.display = onSale ? "block" : "none";
      if (onSale) this.priceSale.innerHTML = ProductPage.formatCents(compare, this.bundleEl.dataset.moneyFormat);
    }
    const atbPrice = document.getElementById("pdp-atb-price");
    if (atbPrice && !atbPrice.hasAttribute("data-price-locked")) atbPrice.innerHTML = formatted;
  }

  /* Shop money format, trailing zeros dropped like money_without_trailing_zeros */
  static formatCents(cents, format = "£{{amount}}") {
    const whole = cents % 100 === 0;
    const amount = (cents / 100).toFixed(whole ? 0 : 2);
    const withCommas = (value, sep, dec) => {
      const [int, frac] = value.split(".");
      return int.replace(/\B(?=(\d{3})+(?!\d))/g, sep) + (frac ? dec + frac : "");
    };
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      if (key === "amount_no_decimals") return withCommas((cents / 100).toFixed(0), ",", ".");
      if (key.includes("comma_separator")) return withCommas(amount, ".", ",");
      return withCommas(amount, ",", ".");
    });
  }

  /* -----------------------------------------------------------------------
     HERO SWIPER (standard PDP — left column of 2-column layout)
  ----------------------------------------------------------------------- */
  initHeroSwiper() {
    const swiperEl = document.getElementById("pdp-hero-swiper");
    const paginationEl = document.getElementById("pdp-pagination");
    if (!swiperEl || typeof Swiper === "undefined") return null;

    return new Swiper(swiperEl, {
      direction: "horizontal",
      loop: false,
      slidesPerView: 1,
      speed: 300,
      pagination: {
        el: paginationEl,
        clickable: true,
        bulletClass: "product-hero__dot",
        bulletActiveClass: "product-hero__dot--active",
      },
    });
  }

  /* -----------------------------------------------------------------------
     QUANTITY SELECTOR (+ / −)
  ----------------------------------------------------------------------- */
  initQuantitySelector() {
    const wrapper = this.productEl.querySelector("[data-quantity-selector]");
    if (!wrapper) return;
    const input = wrapper.querySelector("[data-quantity-input]");
    if (!input) return;

    const min = parseInt(input.min, 10) || 1;

    wrapper.querySelectorAll("[data-qty-action]").forEach((btn) => {
      btn.addEventListener("click", () => {
        const current = parseInt(input.value, 10) || min;
        const next =
          btn.dataset.qtyAction === "increase"
            ? current + 1
            : Math.max(min, current - 1);
        input.value = next;
        input.dispatchEvent(new Event("change", { bubbles: true }));
      });
    });

    input.addEventListener("change", () => {
      const val = parseInt(input.value, 10);
      if (isNaN(val) || val < min) input.value = min;
    });
  }

  /* -----------------------------------------------------------------------
     INFO ACCORDION (shared with configurator layout)
  ----------------------------------------------------------------------- */
  initInfoAccordion() {
    const accordion = document.querySelector("[data-product-accordion]");
    if (!accordion) return;

    const items = [...accordion.querySelectorAll("[data-product-accordion-item]")];
    items.forEach((item) => {
      const trigger = item.querySelector("[data-product-accordion-trigger]");
      if (!trigger) return;
      trigger.addEventListener("click", () => {
        const isOpen = item.classList.contains("is-open");
        // Exclusive accordion — close siblings
        items.forEach((i) => {
          i.classList.remove("is-open");
          const t = i.querySelector("[data-product-accordion-trigger]");
          if (t) t.setAttribute("aria-expanded", "false");
        });
        if (!isOpen) {
          item.classList.add("is-open");
          trigger.setAttribute("aria-expanded", "true");
        }
      });
    });
  }

  /* GET PRODUCT DATA */
  getProductData() {
    const jsonEl = document.getElementById(`ProductData-${this.productId}`);
    if (!jsonEl) return null;

    try {
      const data = JSON.parse(jsonEl.textContent);
      this.variantImages = data.variantImages || {};
      this.formattedPrices = data.formattedPrices || {};
      this.inventoryPolicies = data.inventoryPolicies || {};
      return data.product;
    } catch (e) {
      console.error("Product JSON parse error:", e);
      return null;
    }
  }

  /**
   * Returns true if a variant is sellable — either it has available inventory,
   * or its inventory_policy is "continue" (sell when out of stock).
   * Shopify's product | json can return available: false even when inventory_policy
   * is "continue", so we check both.
   */
  isVariantSellable(variant) {
    if (!variant) return false;
    if (this.inventoryPolicies[variant.id] === "continue") return true;
    return !!variant.available;
  }

  /* VARIANTS */
  initVariantLogic() {
    this.pillGroups = document.querySelectorAll(".product__variant-pills");
    this.selectGroups = document.querySelectorAll(".product__variant-select");
    this.variantInput = document.getElementById("product-select");
    this.priceSale = document.getElementById("product__price-sale");
    this.priceOg = document.getElementById("product__price-og");
    this.addBtn = document.getElementById("product-btn-avail");
    this.soldBtn = document.getElementById("product-btn-sold");

    const urlParams = new URLSearchParams(window.location.search);
    const variantId = urlParams.get("variant");

    let initialVariant =
      (variantId && this.productData.variants.find((v) => v.id == variantId)) ||
      this.productData.variants.find((v) => this.isVariantSellable(v)) ||
      this.productData.variants[0];

    if (!initialVariant) return;

    this.setSelectionFromVariant(initialVariant);
    this.updateVariantUI(initialVariant);

    this.pillGroups.forEach((group) => {
      group.addEventListener("click", (e) => {
        const btn = e.target.closest("button");
        if (!btn) return;
        group
          .querySelectorAll("button")
          .forEach((b) => b.classList.remove("active"));
        btn.classList.add("active");
        this.updateFromSelected();
      });
    });

    this.selectGroups.forEach((select) => {
      select.addEventListener("change", () => this.updateFromSelected());
    });
  }

  /* Returns the currently selected value for an option position (1-based index from options array), or null. */
  getSelectedValue(index) {
    const position = index + 1;
    const pillGroup = document.querySelector(
      `.product__variant-pills[data-option-position="${position}"]`,
    );
    if (pillGroup) {
      return pillGroup.querySelector(".active")?.dataset.value || null;
    }
    const selectGroup = document.querySelector(
      `.product__variant-select[data-option-position="${position}"]`,
    );
    if (selectGroup) {
      return selectGroup.value || null;
    }
    return null;
  }

  updateFromSelected() {
    const selectedOptions = this.productData.options.map((_, i) =>
      this.getSelectedValue(i),
    );

    if (selectedOptions.some((opt) => !opt)) {
      this.showSoldOut();
      return;
    }

    const matchedVariant = this.productData.variants.find((variant) =>
      selectedOptions.every((opt, i) => variant[`option${i + 1}`] === opt),
    );

    if (matchedVariant) {
      this.updateVariantUI(matchedVariant);
    }
  }

  updateVariantUI(variant) {
    this.currentVariant = variant;
    document.querySelectorAll("[data-option-selected]").forEach((el) => {
      el.textContent = variant[`option${el.dataset.optionSelected}`] || "";
    });
    if (this.variantInput) {
      this.variantInput.value = variant.id;
    }

    if (this.isVariantSellable(variant)) {
      this.showAvailable();
    } else {
      this.showSoldOut();
    }

    if (this.priceOg && this.formattedPrices[variant.id]) {
      this.priceOg.innerHTML = this.formattedPrices[variant.id].price;
    }

    if (this.priceSale && this.formattedPrices[variant.id]) {
      if (variant.compare_at_price > variant.price) {
        this.priceSale.style.display = "block";
        this.priceSale.innerHTML = this.formattedPrices[variant.id].compare;
      } else {
        this.priceSale.style.display = "none";
      }
    }

    const atbPrice = document.getElementById("pdp-atb-price");
    if (atbPrice && !atbPrice.hasAttribute("data-price-locked") && this.formattedPrices[variant.id]) {
      atbPrice.innerHTML = this.formattedPrices[variant.id].price;
    }

    this.productData.options.forEach((_, index) => {
      const label = document.querySelector(
        `.active-option-label[data-option-position="${index + 1}"]`,
      );

      if (label) {
        label.textContent = variant[`option${index + 1}`];
      }
    });

    this.updateURL(variant.id);
    this.updateVariantAvailability();
    this.updateGallery(variant);
    this.updateBundle();

    if (typeof syncPurchase === "function") syncPurchase(this.variantInput?.closest("[data-purchase-scope]"));
  }

  showAvailable() {
    if (this.addBtn) this.addBtn.style.display = "";
    if (this.soldBtn) this.soldBtn.style.display = "none";
  }

  showSoldOut() {
    if (this.addBtn) this.addBtn.style.display = "none";
    if (this.soldBtn) this.soldBtn.style.display = "";
  }

  updateVariantAvailability() {
    const selectedOptions = this.productData.options.map((_, i) =>
      this.getSelectedValue(i),
    );

    const isValueSellable = (index, value) => {
      const testOptions = [...selectedOptions];
      testOptions[index] = value;
      const matchingVariant = this.productData.variants.find((variant) =>
        testOptions.every((opt, i) => !opt || variant[`option${i + 1}`] === opt),
      );
      return this.isVariantSellable(matchingVariant);
    };

    this.productData.options.forEach((_, index) => {
      const position = index + 1;

      const pillGroup = document.querySelector(
        `.product__variant-pills[data-option-position="${position}"]`,
      );
      if (pillGroup) {
        pillGroup.querySelectorAll("button").forEach((btn) => {
          btn.classList.toggle(
            "is-out-of-stock",
            !isValueSellable(index, btn.dataset.value),
          );
        });
      }

      const selectGroup = document.querySelector(
        `.product__variant-select[data-option-position="${position}"]`,
      );
      if (selectGroup) {
        selectGroup.querySelectorAll("option").forEach((opt) => {
          opt.disabled = !isValueSellable(index, opt.value);
        });
      }
    });
  }

  updateGallery(variant) {
    if (!this.Swiper) return;

    // Use the variant's featured_image position (1-based) to scroll the
    // carousel to the matching slide. Falls back gracefully if not set.
    const pos = variant?.featured_image?.position;
    if (!pos) return;

    this.Swiper.slideTo(pos - 1);
  }

  updateURL(variantId) {
    const url = new URL(window.location.href);
    url.searchParams.set("variant", variantId);
    window.history.replaceState({}, "", url);
  }

  setSelectionFromVariant(variant) {
    this.productData.options.forEach((_, index) => {
      const position = index + 1;
      const value = variant[`option${position}`];

      const pillGroup = document.querySelector(
        `.product__variant-pills[data-option-position="${position}"]`,
      );
      if (pillGroup) {
        pillGroup.querySelectorAll("button").forEach((btn) => {
          btn.classList.toggle("active", btn.dataset.value === value);
        });
      }

      const selectGroup = document.querySelector(
        `.product__variant-select[data-option-position="${position}"]`,
      );
      if (selectGroup) {
        selectGroup.value = value;
      }
    });
  }

  syncFromURL() {
    const variantId = new URLSearchParams(window.location.search).get(
      "variant",
    );
    if (!variantId) return;

    const variant = this.productData.variants.find((v) => v.id == variantId);
    if (!variant) return;

    this.updateVariantUI(variant);
  }
}

document.addEventListener("DOMContentLoaded", () => {
  new ProductPage();
});
